import { ALLOWED_TOKENS, type AllowedToken } from './validateTemplateContent';

/** Human-readable label for a merge token (e.g. `carer_name` → "carer name"). */
export function humaniseToken(token: string): string {
  return token.replace(/_/g, ' ');
}

/**
 * Curated friendly labels for the known merge tokens, used by the PII-safe "Sent"
 * message-preview placeholder chips (PRO-0620 follow-up). Any token OUTSIDE the
 * allow-list (a well-formed but unknown `{{identifier}}`) falls back to a
 * humanised, sentence-cased token name (e.g. `child_first_name` → "Child first
 * name"). The label never carries a resolved value — it names the placeholder.
 */
const FRIENDLY_TOKEN_LABELS: Record<AllowedToken, string> = {
  carer_name: "Parent or carer's name",
  child_name: "Child's name",
  school_name: 'School name',
  sender_name: "Sender's name",
};

export function friendlyTokenLabel(token: string): string {
  const known = FRIENDLY_TOKEN_LABELS[token as AllowedToken];
  if (known) {
    return known;
  }
  const humanised = humaniseToken(token);
  return humanised.charAt(0).toUpperCase() + humanised.slice(1);
}

/** Canonical insert syntax for a merge token. */
export function formatMergeToken(token: AllowedToken): string {
  return `{{${token}}}`;
}

/** MIME type used when dragging a merge token onto a text field. */
export const MERGE_FIELD_DRAG_TYPE = 'application/x-ucp-merge-token';

export interface MergeFieldCatalogEntry {
  readonly token: AllowedToken;
  readonly label: string;
  readonly description: string;
  readonly insertValue: string;
}

const DESCRIPTIONS: Record<AllowedToken, string> = {
  carer_name: 'The parent or carer’s name for each recipient',
  child_name: 'The child’s name for the pupil record in the audience',
  school_name: 'The school name for the active school context',
  sender_name: 'The name of the staff member sending the message',
};

/** Display metadata for every allowed merge token (PRO-0270). */
export const MERGE_FIELD_CATALOG: readonly MergeFieldCatalogEntry[] = ALLOWED_TOKENS.map(
  (token) => ({
    token,
    label: humaniseToken(token).replace(/\b\w/g, (c) => c.toUpperCase()),
    description: DESCRIPTIONS[token],
    insertValue: formatMergeToken(token),
  }),
);
